// Growth Journey — 12-month WCC360 timeline helpers
import { supabase } from "@/integrations/supabase/client";

export type MilestoneKind =
  | "initial_assessment"
  | "reflection"
  | "quarterly_refresh"
  // Deprecated alias kept so older DB rows still render
  | "mid_year_refresh"
  | "annual_refresh";

export type Milestone = {
  id: string;
  kind: MilestoneKind;
  month_index: number;
  completed_at: string;
  payload: Record<string, unknown>;
};

export type Membership = {
  id: string;
  plan: "individual" | "family";
  started_at: string;
  expires_at: string;
  is_active: boolean;
};

export type JourneyPayload = {
  membership: Membership | null;
  milestones: Milestone[];
};

export async function fetchMyJourney(): Promise<JourneyPayload> {
  const { data, error } = await supabase.rpc("get_my_journey");
  if (error || !data) return { membership: null, milestones: [] };
  const payload = data as unknown as JourneyPayload;
  return {
    membership: payload.membership ?? null,
    milestones: payload.milestones ?? [],
  };
}

export type ScheduledStep = {
  monthIndex: number; // 0..12
  kind: MilestoneKind;
  title: string;
  blurb: string;
  scheduledFor: Date;
  status: "completed" | "current" | "upcoming" | "locked";
  completedAt?: string;
  payload?: Record<string, unknown>;
};

const KIND_TITLE: Record<MilestoneKind, string> = {
  initial_assessment: "Initial WCC360 Assessment",
  reflection: "Monthly Reflection",
  quarterly_refresh: "Quarterly Career Refresh",
  mid_year_refresh: "Quarterly Career Refresh",
  annual_refresh: "Annual Final Assessment",
};

const KIND_BLURB: Record<MilestoneKind, string> = {
  initial_assessment:
    "Your baseline — strengths, interests, values mapped to 5,000+ careers of the future.",
  reflection:
    "Capture wins, learning, new interests and shifting goals. Your insights stay current.",
  quarterly_refresh:
    "A fresh WCC360 assessment — re-map your themes to the latest careers and industries.",
  mid_year_refresh:
    "A fresh WCC360 assessment — re-map your themes to the latest careers and industries.",
  annual_refresh:
    "A full re-evaluation of who you've become — and where you're heading next.",
};

function kindForMonth(i: number): MilestoneKind {
  if (i === 0) return "initial_assessment";
  if (i === 3 || i === 6 || i === 9) return "quarterly_refresh";
  if (i === 12) return "annual_refresh";
  return "reflection";
}

export function addMonths(d: Date, months: number): Date {
  const out = new Date(d);
  out.setMonth(out.getMonth() + months);
  return out;
}

export function buildSchedule(
  membership: Membership | null,
  milestones: Milestone[],
  now: Date = new Date(),
): ScheduledStep[] {
  // If no membership, fall back to a preview schedule starting today
  const start = membership ? new Date(membership.started_at) : new Date();
  const completedByKey = new Map<string, Milestone>();
  for (const m of milestones) {
    completedByKey.set(`${m.kind}:${m.month_index}`, m);
  }

  const steps: ScheduledStep[] = [];
  for (let i = 0; i <= 12; i++) {
    const kind = kindForMonth(i);
    const scheduledFor = addMonths(start, i);
    const key = `${kind}:${i}`;
    const completed = completedByKey.get(key);
    let status: ScheduledStep["status"] = "locked";
    if (completed) status = "completed";
    else if (scheduledFor <= now) status = "current";
    else status = "upcoming";
    steps.push({
      monthIndex: i,
      kind,
      title: KIND_TITLE[kind],
      blurb: KIND_BLURB[kind],
      scheduledFor,
      status,
      completedAt: completed?.completed_at,
      payload: completed?.payload,
    });
  }
  return steps;
}

// -- Badges (demo-seeded preview where empty) -------------------------------
export type Badge = {
  key: string;
  label: string;
  emoji: string;
  blurb: string;
  earned: boolean;
};

export function deriveBadges(steps: ScheduledStep[]): Badge[] {
  const completed = steps.filter((s) => s.status === "completed").length;
  const reflections = steps.filter(
    (s) => s.kind === "reflection" && s.status === "completed",
  ).length;
  const refreshes = steps.filter(
    (s) =>
      (s.kind === "mid_year_refresh" || s.kind === "quarterly_refresh" || s.kind === "annual_refresh") &&
      s.status === "completed",
  ).length;

  const e = (cond: boolean) => cond;
  return [
    { key: "explorer", label: "Explorer", emoji: "🧭", blurb: "Started the journey", earned: e(completed >= 1) },
    { key: "creator", label: "Creator", emoji: "🎨", blurb: "Logged 1 reflection", earned: e(reflections >= 1) },
    { key: "navigator", label: "Navigator", emoji: "🗺️", blurb: "3 reflections in", earned: e(reflections >= 3) },
    { key: "future_builder", label: "Future Builder", emoji: "🚀", blurb: "Completed mid-year refresh", earned: e(refreshes >= 1) },
    { key: "visionary", label: "Visionary", emoji: "🔮", blurb: "Annual refresh complete", earned: e(refreshes >= 2) },
    { key: "purpose_champion", label: "Purpose Champion", emoji: "💎", blurb: "6 months of reflection", earned: e(reflections >= 6) },
    { key: "climate_warrior", label: "Climate Warrior", emoji: "🌱", blurb: "Climate themes emerging", earned: false },
    { key: "ai_explorer", label: "AI Explorer", emoji: "🤖", blurb: "AI-curious", earned: false },
    { key: "space_pioneer", label: "Space Pioneer", emoji: "🛰️", blurb: "Space-tech curious", earned: false },
    { key: "entrepreneur", label: "Entrepreneur", emoji: "💼", blurb: "Builder mindset", earned: false },
    { key: "leader", label: "Leader", emoji: "🏆", blurb: "Leadership signals", earned: false },
  ];
}

// -- Growth chart (real where possible, demo-seeded preview overlay) --------
export type GrowthPoint = {
  month: string;
  Clarity: number;
  Skills: number;
  Confidence: number;
  isPreview: boolean;
};

const MONTH_LABEL = ["M0","M1","M2","M3","M4","M5","M6","M7","M8","M9","M10","M11","M12"];

export function buildGrowthSeries(steps: ScheduledStep[]): GrowthPoint[] {
  // Base demo trajectory (gentle upward curve) that real completions amplify.
  return steps.map((s, i) => {
    const baseClarity = 40 + i * 3.5;
    const baseSkills = 32 + i * 4;
    const baseConfidence = 38 + i * 3;
    const boost = s.status === "completed" ? 6 : 0;
    return {
      month: MONTH_LABEL[i] ?? `M${i}`,
      Clarity: Math.min(100, Math.round(baseClarity + boost)),
      Skills: Math.min(100, Math.round(baseSkills + boost * 1.1)),
      Confidence: Math.min(100, Math.round(baseConfidence + boost * 0.9)),
      isPreview: s.status !== "completed",
    };
  });
}

export function daysBetween(a: Date, b: Date): number {
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86_400_000));
}

export function formatMonth(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

// -- Future readiness (real signals) ----------------------------------------
export type ReadinessSignals = {
  scoredAssessments: number;
  reflections: number;
  refreshes: number;
  earnedBadges: number;
  totalXp: number;
  currentStreak: number;
};

export type ReadinessBreakdown = {
  score: number; // 0..100
  parts: { label: string; points: number; max: number; detail: string }[];
};

export async function fetchReadinessSignals(
  steps: ScheduledStep[],
  badges: Badge[],
): Promise<ReadinessSignals> {
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth?.user?.id;
  if (!uid) {
    return {
      scoredAssessments: 0,
      reflections: 0,
      refreshes: 0,
      earnedBadges: badges.filter((b) => b.earned).length,
      totalXp: 0,
      currentStreak: 0,
    };
  }

  const [assessRes, reflRes, xpRes] = await Promise.all([
    supabase
      .from("assessments")
      .select("id", { count: "exact", head: true })
      .eq("user_id", uid)
      .eq("status", "scored"),
    supabase
      .from("monthly_reflections")
      .select("id", { count: "exact", head: true })
      .eq("user_id", uid),
    supabase
      .from("user_xp")
      .select("total_xp, current_streak")
      .eq("user_id", uid)
      .maybeSingle(),
  ]);

  const refreshes = steps.filter(
    (s) =>
      (s.kind === "mid_year_refresh" || s.kind === "quarterly_refresh" || s.kind === "annual_refresh") &&
      s.status === "completed",
  ).length;

  return {
    scoredAssessments: assessRes.count ?? 0,
    reflections: reflRes.count ?? 0,
    refreshes,
    earnedBadges: badges.filter((b) => b.earned).length,
    totalXp: xpRes.data?.total_xp ?? 0,
    currentStreak: xpRes.data?.current_streak ?? 0,
  };
}

export function computeReadiness(s: ReadinessSignals): ReadinessBreakdown {
  const baseline = 15;
  const assessPts = s.scoredAssessments > 0 ? 20 : 0;
  const reflPts = Math.min(30, s.reflections * 3);
  const refreshPts = Math.min(20, s.refreshes * 5);
  const badgePts = Math.min(10, s.earnedBadges * 2);
  const xpPts = Math.min(10, Math.floor(s.totalXp / 100));
  const streakPts = Math.min(5, s.currentStreak);

  const score = Math.min(
    100,
    baseline + assessPts + reflPts + refreshPts + badgePts + xpPts + streakPts,
  );

  return {
    score,
    parts: [
      { label: "Baseline", points: baseline, max: 15, detail: "Starting score for every member" },
      {
        label: "Assessment scored",
        points: assessPts,
        max: 20,
        detail: s.scoredAssessments > 0 ? "Baseline mapped to careers" : "Complete your WCC360 to unlock",
      },
      {
        label: "Monthly reflections",
        points: reflPts,
        max: 30,
        detail: `${s.reflections} logged · 3 pts each (cap 30)`,
      },
      {
        label: "Future refreshes",
        points: refreshPts,
        max: 20,
        detail: `${s.refreshes} of 4 completed · 5 pts each (quarterly + annual)`,
      },
      {
        label: "Badges earned",
        points: badgePts,
        max: 10,
        detail: `${s.earnedBadges} badges · 2 pts each (cap 10)`,
      },
      {
        label: "XP momentum",
        points: xpPts,
        max: 10,
        detail: `${s.totalXp.toLocaleString()} XP · 1 pt per 100 XP (cap 10)`,
      },
      {
        label: "Reflection streak",
        points: streakPts,
        max: 5,
        detail: `${s.currentStreak}-day streak · 1 pt per day (cap 5)`,
      },
    ],
  };
}

